import { formatDistanceToNowStrict } from 'date-fns'

import { Text, Tooltip } from '@buffer-mono/popcorn'

import { useAccountId } from '~publish/contexts/AccountContext'
import { useDateTimeFormatter } from '~publish/hooks/useDateTimeFormatter'

import styles from './ContentItemCard.module.css'

import type { JSX } from 'react'

type ContentItemCardAuthorProps = {
  createdAt: string
  author: {
    id: string
    name?: string | null
    email: string
  } | null
}

/**
 * Footer meta for a channel-less draft: "You created this 2 hours ago" (or the
 * teammate's name/email when it wasn't you). Mirrors PostCardAuthor, but drafts
 * carry no PostCardContext, so author and createdAt come in as props.
 */
export function ContentItemCardAuthor({
  createdAt,
  author,
}: ContentItemCardAuthorProps): JSX.Element {
  const accountId = useAccountId()
  const dateTimeFormatter = useDateTimeFormatter()

  const isAuthor = author?.id === accountId
  const authorNameOrEmail = author?.name || author?.email

  const relativeTime = formatDistanceToNowStrict(new Date(createdAt))

  const who = author ? (isAuthor ? 'You' : authorNameOrEmail) : 'Someone'

  const tooltipContent = (
    <>
      <b>{who}</b> created this on{' '}
      {dateTimeFormatter(createdAt, { includeTimeZone: false })}
    </>
  )

  return (
    <Tooltip content={tooltipContent} delay={1000}>
      <div className={styles.footerCreator}>
        <Text color="subtle">
          <Text weight="medium" color="subtle">
            {who}
          </Text>{' '}
          created this {relativeTime} ago
        </Text>
      </div>
    </Tooltip>
  )
}
